import { useId } from 'react';

import type { AiChatChart, AiChatChartType, AiChatSeries } from './types';

/**
 * The colours series are drawn in, in order.
 *
 * They are theme tokens rather than literals, so both themes get a legible
 * chart from one stylesheet, and series index three wraps around to the first
 * colour — which the legend still distinguishes by name, because a chart that
 * told one series from another by colour alone would be unreadable to anybody
 * who cannot see the difference.
 */
const SERIES_TOKENS = ['--accent-base', '--tone-info-line', '--tone-warning-line'] as const;

/** The drawing's coordinate system. One unit is one CSS pixel at full panel width. */
const VIEW_WIDTH = 320;
const VIEW_HEIGHT = 180;
const PAD_TOP = 22;
const PAD_RIGHT = 10;
const PAD_BOTTOM = 30;
const PAD_LEFT = 10;

/** The area the marks are drawn in. */
const PLOT = {
  left: PAD_LEFT,
  right: VIEW_WIDTH - PAD_RIGHT,
  top: PAD_TOP,
  bottom: VIEW_HEIGHT - PAD_BOTTOM,
  width: VIEW_WIDTH - PAD_LEFT - PAD_RIGHT,
  height: VIEW_HEIGHT - PAD_TOP - PAD_BOTTOM,
} as const;

const LINE_WIDTH = 2;
const AXIS_WIDTH = 1;
const POINT_RADIUS = 2.2;
const LABEL_SIZE = 9;
const UNIT_SIZE = 8;
const VALUE_SIZE = 8;
/** How far a value label sits from the end of its bar or point. */
const VALUE_GAP = 3;
/** Roughly how wide one character is at {@link LABEL_SIZE}, for budgeting labels. */
const CHAR_WIDTH = 5.5;
/** The share of a category slot a bar group fills. */
const BAR_FILL = 0.7;

/** The colour of one series, as a CSS custom property reference. */
function paint(index: number): string {
  const token = SERIES_TOKENS[index % SERIES_TOKENS.length] ?? SERIES_TOKENS[0];
  return `var(${token})`;
}

/** The range the drawing spans. It always includes zero: a bar chart needs a zero baseline. */
interface Domain {
  readonly min: number;
  readonly max: number;
}

/** The vertical extent of a set of values, with zero always inside it. */
function domainOf(values: readonly number[]): Domain {
  let min = 0;
  let max = 0;
  for (const value of values) {
    min = Math.min(min, value);
    max = Math.max(max, value);
  }
  // A flat series — every value zero, or one value repeated — still needs a
  // range, or the scale divides by zero where the baseline should be.
  if (max === min) max = min + 1;
  return { min, max };
}

/** Where a value sits vertically. */
function yFor(value: number, domain: Domain): number {
  return PLOT.top + ((domain.max - value) / (domain.max - domain.min)) * PLOT.height;
}

/** How wide one category slot is. */
function slotWidth(count: number): number {
  return PLOT.width / count;
}

/** The horizontal centre of one category slot. */
function slotCentre(index: number, count: number): number {
  return PLOT.left + slotWidth(count) * (index + 0.5);
}

/** Roughly how many characters fit in one category slot. */
function labelBudget(slot: number): number {
  return Math.max(4, Math.min(14, Math.floor(slot / CHAR_WIDTH)));
}

/**
 * Cuts an axis label down to a slot. The description keeps the whole label, so
 * nothing is lost — it is moved to where there is room for it.
 *
 * @param label the full label
 * @param budget how many characters the slot can hold
 * @returns the label, shortened with an ellipsis when it had to be
 */
export function shortenLabel(label: string, budget: number): string {
  return label.length <= budget ? label : `${label.slice(0, Math.max(1, budget - 1))}…`;
}

/** One value, as the description writes it. */
function formatValue(value: number): string {
  return String(value);
}

const VALUE_FORMAT = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * A value as it is printed on the drawing: short enough for a narrow bar.
 * The description keeps the exact figure.
 */
export function valueLabel(value: number): string {
  return VALUE_FORMAT.format(value);
}

/** Where a value label goes: above a mark that points up, below one that hangs down. */
function valueLabelY(value: number, y: number): number {
  return value < 0 ? y + VALUE_GAP + VALUE_SIZE : y - VALUE_GAP;
}

/**
 * The text alternative for a chart, naming every value.
 *
 * It is not a summary. "Jobs by crew" alone would leave a screen reader user
 * asking for the numbers and getting a shrug, so every category and every value
 * is in here, series by series — which is also what makes it usable as the
 * fallback text when the drawing itself does not help.
 *
 * @param chart a chart, already read as coherent
 * @returns one line of text, e.g. `Jobs by crew: Ray Oduya 4, Unassigned 10`
 */
export function describeChart(chart: AiChatChart): string {
  const title = typeof chart.title === 'string' ? chart.title.trim() : '';
  const heading = title === '' ? 'Chart' : title;
  const pairs = (series: AiChatSeries): string =>
    series.values
      .map((value, index) => `${chart.categories[index] ?? ''} ${formatValue(value)}`)
      .join(', ');
  const [first] = chart.series;
  // One series needs no label of its own: the heading already says what the
  // numbers are. Two or more do, because "which of these is which" is exactly
  // the question a drawing answered with colour alone leaves unanswered.
  if (chart.series.length === 1 && first !== undefined) return `${heading}: ${pairs(first)}`;
  return `${heading}. ${chart.series
    .map((series) => `${series.label}: ${pairs(series)}`)
    .join('; ')}`;
}

/** A list of strings, or nothing when the value is not one. */
function stringList(value: unknown): readonly string[] | null {
  if (!Array.isArray(value)) return null;
  const items: string[] = [];
  for (const item of value as unknown[]) {
    if (typeof item !== 'string') return null;
    items.push(item);
  }
  return items;
}

/** A list of plottable numbers, or nothing when the value is not one. */
function numberList(value: unknown): readonly number[] | null {
  if (!Array.isArray(value)) return null;
  const items: number[] = [];
  for (const item of value as unknown[]) {
    if (typeof item !== 'number' || !Number.isFinite(item)) return null;
    items.push(item);
  }
  return items;
}

/** One series, or nothing when it cannot be drawn against this many categories. */
function readableSeries(value: unknown, categories: number): AiChatSeries | null {
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as { label?: unknown; values?: unknown };
  if (typeof candidate.label !== 'string') return null;
  const values = numberList(candidate.values);
  // A series whose length disagrees with the axis cannot be plotted against it.
  // Dropping it is the only honest option: padding it would invent values, and
  // truncating it would silently lose them.
  if (values?.length !== categories) return null;
  return { label: candidate.label, values };
}

/** A chart that can actually be drawn. */
interface ReadableChart {
  readonly type: AiChatChartType;
  readonly title: string;
  readonly unit: string;
  readonly categories: readonly string[];
  readonly series: readonly AiChatSeries[];
}

/**
 * Reads a chart payload, keeping only what can be drawn.
 *
 * The series that agree with the axis are drawn; one that does not is dropped
 * rather than guessed at. When nothing is left, the answer shows its prose
 * alone, which is a smaller failure than a chart whose bars point at the wrong
 * labels.
 */
function readableChart(chart: AiChatChart): ReadableChart | null {
  const payload = chart as Partial<Record<keyof AiChatChart, unknown>> | null | undefined;
  if (payload === null || payload === undefined) return null;
  if (payload.type !== 'bar' && payload.type !== 'line') return null;
  const categories = stringList(payload.categories);
  if (categories === null || categories.length === 0) return null;
  if (!Array.isArray(payload.series)) return null;
  const series = (payload.series as unknown[])
    .map((one) => readableSeries(one, categories.length))
    .filter((one): one is AiChatSeries => one !== null);
  if (series.length === 0) return null;
  return {
    type: payload.type,
    title: typeof payload.title === 'string' ? payload.title.trim() : '',
    unit: typeof payload.unit === 'string' ? payload.unit.trim() : '',
    categories,
    series,
  };
}

export interface AssistantChartProps {
  /** The chart the answer carried. */
  readonly chart: AiChatChart;
}

/**
 * A chart from an assistant answer, drawn as inline SVG.
 *
 * There is no charting dependency here on purpose. The assistant panel is
 * narrow, the archetypes are two, and the guarantees that matter are about what
 * the drawing *says* rather than how it is drawn: the values are in the
 * description whether or not the picture renders, the series are named rather
 * than only coloured, and a negative value hangs below a zero baseline instead
 * of being clipped to the floor.
 *
 * Nothing animates and nothing transitions. A chart that grew when the answer
 * arrived would be a chart that had to be waited for.
 *
 * @param props see {@link AssistantChartProps}
 * @returns the chart, or nothing when the payload cannot be drawn coherently
 */
export function AssistantChart({ chart }: AssistantChartProps): React.JSX.Element | null {
  const titleId = `${useId()}-title`;
  const descId = `${useId()}-desc`;
  const readable = readableChart(chart);
  if (readable === null) return null;

  const domain = domainOf(readable.series.flatMap((series) => [...series.values]));
  const baseline = yFor(0, domain);
  const count = readable.categories.length;
  const slot = slotWidth(count);
  const budget = labelBudget(slot);
  const description = describeChart(readable);
  const name = readable.title === '' ? 'Assistant chart' : readable.title;

  return (
    <figure className="fo-answer-chart">
      {readable.title === '' ? null : (
        <figcaption className="fo-answer-chart__title">{readable.title}</figcaption>
      )}
      <svg
        className="fo-answer-chart__svg"
        viewBox={`0 0 ${String(VIEW_WIDTH)} ${String(VIEW_HEIGHT)}`}
        role="img"
        aria-labelledby={titleId}
        aria-describedby={descId}
        preserveAspectRatio="xMidYMid meet"
      >
        <title id={titleId}>{name}</title>
        {/* The description names every value, so the chart is readable when the
            drawing is not: read aloud, printed, or looked at by somebody the
            colours do not help. */}
        <desc id={descId}>{description}</desc>

        {readable.unit === '' ? null : (
          <text
            className="fo-answer-chart__unit"
            x={PLOT.left}
            y={PAD_TOP - 10}
            fontSize={UNIT_SIZE}
            style={{ fill: 'var(--ink-muted)' }}
          >
            {shortenLabel(readable.unit, 40)}
          </text>
        )}

        {readable.categories.map((category, index) => (
          <text
            key={`${String(index)}-${category}`}
            className="fo-answer-chart__category"
            x={slotCentre(index, count)}
            y={PLOT.bottom + 14}
            textAnchor="middle"
            fontSize={LABEL_SIZE}
            style={{ fill: 'var(--ink-secondary)' }}
          >
            {shortenLabel(category, budget)}
          </text>
        ))}

        <line
          className="fo-answer-chart__baseline"
          x1={PLOT.left}
          x2={PLOT.right}
          y1={baseline}
          y2={baseline}
          strokeWidth={AXIS_WIDTH}
          style={{ stroke: 'var(--line-strong)' }}
        />

        {readable.type === 'bar'
          ? readable.series.flatMap((series, seriesIndex) =>
              series.values.map((value, index) => {
                const y = yFor(value, domain);
                const width = (slot * BAR_FILL) / readable.series.length;
                const group =
                  PLOT.left + slot * index + (slot - width * readable.series.length) / 2;
                // Each bar carries its own figure: without an axis scale, a bar
                // whose value has to be guessed from its height is a bar nobody
                // can quote.
                return (
                  <g key={`${series.label}-${String(index)}`}>
                    <rect
                      className="fo-answer-chart__bar"
                      data-series={seriesIndex}
                      x={group + width * seriesIndex}
                      y={Math.min(y, baseline)}
                      width={width}
                      height={Math.abs(y - baseline)}
                      rx={1.5}
                      style={{ fill: paint(seriesIndex) }}
                    />
                    <text
                      className="fo-answer-chart__value"
                      x={group + width * seriesIndex + width / 2}
                      y={valueLabelY(value, y)}
                      textAnchor="middle"
                      fontSize={VALUE_SIZE}
                      style={{ fill: 'var(--ink-secondary)' }}
                    >
                      {valueLabel(value)}
                    </text>
                  </g>
                );
              }),
            )
          : readable.series.map((series, seriesIndex) => (
              <g key={series.label} className="fo-answer-chart__series">
                <path
                  className="fo-answer-chart__line"
                  data-series={seriesIndex}
                  d={series.values
                    .map(
                      (value, index) =>
                        `${index === 0 ? 'M' : 'L'} ${String(slotCentre(index, count))} ${String(
                          yFor(value, domain),
                        )}`,
                    )
                    .join(' ')}
                  fill="none"
                  strokeWidth={LINE_WIDTH}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  style={{ stroke: paint(seriesIndex) }}
                />
                {series.values.map((value, index) => (
                  <g key={String(index)}>
                    <circle
                      className="fo-answer-chart__point"
                      data-series={seriesIndex}
                      cx={slotCentre(index, count)}
                      cy={yFor(value, domain)}
                      r={POINT_RADIUS}
                      style={{ fill: paint(seriesIndex) }}
                    />
                    <text
                      className="fo-answer-chart__value"
                      x={slotCentre(index, count)}
                      y={valueLabelY(value, yFor(value, domain))}
                      textAnchor="middle"
                      fontSize={VALUE_SIZE}
                      style={{ fill: 'var(--ink-secondary)' }}
                    >
                      {valueLabel(value)}
                    </text>
                  </g>
                ))}
              </g>
            ))}
      </svg>
      <ul className="fo-answer-chart__legend">
        {readable.series.map((series, index) => (
          <li key={series.label} className="fo-answer-chart__legend-item">
            <span
              className="fo-answer-chart__swatch"
              data-series={index}
              aria-hidden="true"
              style={{ background: paint(index) }}
            />
            {series.label}
          </li>
        ))}
      </ul>
    </figure>
  );
}
